import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faLink, faUnlink } from '@fortawesome/free-solid-svg-icons';
import {
  fetchLinkableHMSEntities, fetchLinkableCRMProfiles, fetchLinkableOffices,
  updateEntity
} from '../../../services/corporateStructureService';
import './CreateGroupModal.css';

const LINK_CONFIG = {
  hms: { title: 'Entity', field: 'linked_hms_entity', fetchFn: fetchLinkableHMSEntities, currentField: 'linked_hms_entity_name' },
  crm: { title: 'CRM Profile', field: 'linked_crm_profile', fetchFn: fetchLinkableCRMProfiles, currentField: 'linked_crm_profile_name' },
  office: { title: 'Office', field: 'linked_office', fetchFn: fetchLinkableOffices, currentField: 'linked_office_name' },
};

const LinkEntityModal = ({ entityId, linkType, currentEntity, onClose, onSaved }) => {
  const config = LINK_CONFIG[linkType];
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const currentLink = currentEntity?.[config.currentField];

  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoading(true);
        const data = await config.fetchFn();
        setOptions(data);
      } catch (err) {
        console.error('Failed to load linkable resources:', err);
        setError('Failed to load available options');
      } finally {
        setLoading(false);
      }
    };
    loadOptions();
  }, [config]);

  const handleLink = async () => {
    if (!selectedId) return;
    try {
      setSaving(true);
      setError(null);
      await updateEntity(entityId, { [config.field]: parseInt(selectedId) });
      onSaved();
    } catch (err) {
      console.error('Failed to link:', err);
      setError('Failed to link entity');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    try {
      setSaving(true);
      setError(null);
      await updateEntity(entityId, { [config.field]: null });
      onSaved();
    } catch (err) {
      console.error('Failed to unlink:', err);
      setError('Failed to unlink entity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sg-modal-overlay" onClick={onClose}>
      <div className="sg-modal" onClick={e => e.stopPropagation()} style={{ width: 420 }}>
        <div className="sg-modal-header">
          <h3>
            <FontAwesomeIcon icon={faLink} />
            Link {config.title}
          </h3>
          <button className="edp-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="sg-modal-body">
          {error && <div className="sg-modal-error">{error}</div>}

          {currentLink && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f0f5eb', borderRadius: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#4a6b25', fontWeight: 600 }}>
                Currently linked: {currentLink}
              </span>
              <button className="sg-btn sg-btn-danger" onClick={handleUnlink} disabled={saving} style={{ padding: '4px 10px', fontSize: 11 }}>
                <FontAwesomeIcon icon={saving ? faSpinner : faUnlink} spin={saving} />
                Unlink
              </button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 20, color: '#94a37e' }}>
              <FontAwesomeIcon icon={faSpinner} spin /> Loading...
            </div>
          ) : (
            <label className="sg-modal-label">
              Select {config.title}
              <select className="sg-modal-select" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
                <option value="">-- Select --</option>
                {options.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </select>
              {options.length === 0 && (
                <span style={{ fontSize: 11, color: '#94a37e', marginTop: 4 }}>No unlinked {config.title.toLowerCase()} records available</span>
              )}
            </label>
          )}
        </div>
        <div className="sg-modal-footer">
          <button type="button" className="sg-btn sg-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="sg-btn sg-btn-primary" onClick={handleLink} disabled={saving || !selectedId}>
            <FontAwesomeIcon icon={saving ? faSpinner : faLink} spin={saving} />
            {saving ? 'Linking...' : 'Link'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkEntityModal;
